import { CreditCardIcon, GiftIcon, PauseIcon, PlayIcon, RepeatIcon, SkipForwardIcon, XIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { DeleteButton } from '../ui/DeleteButton';
import { useAdmin } from '../AdminContext';
import { btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { deliveriesUntilGift } from '../../utils/account';
import { giftBoxIndex, subscriptionCadences, subscriptionRefill } from '../../utils/refill';
import { dayLong, dayShort } from '../../core/format/dates';
import { euro } from '../../core/format/currency';
import { POSTPONE_DAYS } from '../../core/rules/subscription';
import type { SubscriptionStatus } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';
const statusBadge: Record<SubscriptionStatus, {
  tone: 'success' | 'soft' | 'neutral';
  label: string;
}> = {
  actief: {
    tone: 'success',
    label: 'Actief'
  },
  gepauzeerd: {
    tone: 'soft',
    label: 'Gepauzeerd'
  },
  gestopt: {
    tone: 'neutral',
    label: 'Gestopt'
  }
};

/**
 * Het Bewust Abonnement van één klant — dezelfde acties als in Mijn omgeving,
 * zodat Sarah het aan de telefoon of in de salon voor haar kan regelen.
 */
export function CustomerSubscription({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account
  } = ctl.file;
  const sub = account.subscription;
  const name = account.customer.firstName;
  if (!sub) {
    return <EmptyState icon={RepeatIcon} title={`${name} heeft geen abonnement`} text="Een abonnement start de klant zelf, in de winkelmand of in Mijn omgeving. Zo blijft het haar keuze." data-id="element-1337" data-mp="31sudxzk40phk" />;
  }
  const act = ctl.subscription;
  const active = sub.status === 'actief';
  const policySub = site.policy.subscription;
  // Slim bijvullen: per doos alleen wat op raakt. Zo ziet Sarah wat ze straks inpakt.
  const refill = subscriptionRefill(sub, index.productById, policySub, 3);
  const nextBox = refill.boxes[0];
  const cadences = subscriptionCadences(sub, index.productById, policySub);
  const untilGift = deliveriesUntilGift(sub);
  const lastItem = sub.items.length <= 1;
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-1338" data-mp="39b26f7ox57gz">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-1339" data-mp="35btdg51pddmh">
        <Panel title="Volgende levering" actions={<Badge tone={statusBadge[sub.status].tone} data-id="element-1341" data-mp="30cqcavoufrjs">{statusBadge[sub.status].label}</Badge>} data-id="element-1340" data-mp="359790ea3ze8h">
          <p className="font-display text-[26px] font-light capitalize leading-tight text-ink" data-id="element-1342" data-mp="2vcko5phs8n6g">
            {sub.status === 'gestopt' ? 'Geen leveringen meer' : dayLong(sub.nextDelivery)}
          </p>
          {sub.paymentIssue && sub.status === 'actief' && <p className="mt-2 flex items-start gap-1.5 text-[13px] text-clay" data-id="element-1343" data-mp="2meieymnwao83">
              <CreditCardIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-1344" data-mp="olv12szw1we5" />
              <span data-id="element-1345" data-mp="kiyd0mrkx82t">
                {sub.paymentIssue.reason === 'verlopen' ? 'De betaling voor deze levering is verlopen.' : 'De betaling voor deze levering lukte niet.'} {name} ziet
                in Mijn omgeving een knop om opnieuw te betalen. Niet inpakken tot hij binnen is (order {sub.paymentIssue.orderId}).
              </span>
            </p>}
          {sub.status === 'gepauzeerd' && sub.pausedUntil && <p className="mt-1 text-[13px] text-muted" data-id="element-1346" data-mp="1jk383scnuzwz">Gepauzeerd tot en met {dayLong(sub.pausedUntil)}.</p>}
          {active && <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted" data-id="element-1347" data-mp="1ojpna7c1uq8f">
              <GiftIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-1348" data-mp="1sjgkotw304lx" />
              {untilGift === 1 ? `In deze doos: ${policySub.giftDescription}` : `Cadeau over ${untilGift} dozen`} · {sub.deliveriesDone} verstuurd
            </p>}

          <div className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-id="element-1349" data-mp="38yhsy1kk62n7">
            {active && <>
                <button type="button" onClick={() => act({
              type: 'skip'
            }, 'Levering overgeslagen')} className={btnSecondary} data-id="element-1350" data-mp="37gszvgy9y1vs">
                  <SkipForwardIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1351" data-mp="32k2j0renb29b" />
                  Sla over
                </button>
                <button type="button" onClick={() => act({
              type: 'postpone',
              days: POSTPONE_DAYS
            }, 'Een week opgeschoven')} className={btnSecondary} data-id="element-1352" data-mp="3cgxdmz7ssiib">
                  Week later
                </button>
                {site.policy.subscription.pauseOptions.map(o => <button key={o.weeks} type="button" onClick={() => act({
              type: 'pause',
              weeks: o.weeks
            }, `Gepauzeerd voor ${o.label}`)} aria-label={`Pauzeer ${o.label}`} className={btnSecondary} data-id="element-1353" data-mp="3lfj6bn6zhdwt">
                    <PauseIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1354" data-mp="82qwut4q61j5" />
                    {o.label}
                  </button>)}
              </>}
            {!active && <button type="button" onClick={() => act({
            type: 'resume'
          }, 'Abonnement loopt weer')} className={btnSecondary} data-id="element-1355" data-mp="wnlur2lkr2pn">
                <PlayIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1356" data-mp="in1as33xb0uk" />
                Hervatten
              </button>}
          </div>
        </Panel>

        <Panel title="Om bij te vullen" description={nextBox && nextBox.lines.length ? `Per doos alleen wat op raakt. Volgende doos ${euro(nextBox.total, {
        cents: true
      })}, altijd zonder verzendkosten.` : 'Per doos alleen wat op raakt. De eerstvolgende doos is nog leeg: niets raakt op.'} flush data-id="element-1357" data-mp="8wq80jvz6xp9">
          <ul className="divide-y divide-line" data-id="element-1358" data-mp="3m41deof22jax">
            {sub.items.map(item => {
            const p = index.productById(item.productId);
            const productName = p?.name ?? item.productId;
            const plan = refill.items.find(i => i.productId === item.productId);
            const unknown = refill.unknown.includes(item.productId);
            return <li key={item.productId} className="flex items-center gap-3 px-4 py-3 sm:px-5" data-id="element-1359" data-mp="1mx308tzd4rle">
                  {p ? <ProductImage src={p.image} className="h-10 w-10 shrink-0 rounded p-0.5" data-id="element-1360" data-mp="1iwsn8xd5l0x1" /> : <span className="h-10 w-10 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-1361" data-mp="2fli4clgyhphy" />}
                  <span className="min-w-0 flex-1" data-id="element-1362" data-mp="2anqahlwr3tb4">
                    <span className="block truncate text-[14px] text-ink" data-id="element-1363" data-mp="26kkagw08eyd3">{productName}</span>
                    {p && <span className="block text-[12px] text-muted" data-id="element-1364" data-mp="21lh4988imm3j">
                        {euro(p.price)}
                        {plan && !unknown && ` · gaat ± ${plan.lastsWeeks} weken mee · op ± ${dayShort(plan.runsOutOn)}`}
                      </span>}
                    {unknown && <span className="block text-[12px] text-clay" data-id="element-1365" data-mp="31wa0cvctugzc">Geen “gaat ± … mee” in de catalogus: gaat elke doos mee. Vul het aan bij Aanbod › Producten.</span>}
                  </span>
                  <button type="button" onClick={() => act({
                type: 'removeItem',
                productId: item.productId
              })} disabled={lastItem} title={lastItem ? 'Het laatste product blijft staan: pauzeer of stop in plaats daarvan' : undefined} aria-label={`${p?.name ?? 'Product'} uit het abonnement halen`} className={btnIcon} data-id="element-1366" data-mp="3at0xlb3481vi">
                    <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1367" data-mp="2eagjsk6a15z" />
                  </button>
                </li>;
          })}
          </ul>
          {sub.extras.length > 0 && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-1368" data-mp="2476ze1kbq80n">
              Eenmalig erbij: <span className="text-ink" data-id="element-1369" data-mp="1l8lnmacuspag">{sub.extras.map(id => index.productById(id)?.name ?? id).join(', ')}</span>
            </p>}
        </Panel>
      </div>

      <div className="min-w-0 space-y-6" data-id="element-1370" data-mp="1u1owe5ck0dyr">
        <Panel title="Ritme" data-id="element-1371" data-mp="1u4ukpidczbtf">
          <select value={sub.intervalWeeks} onChange={e => act({
          type: 'setInterval',
          weeks: Number(e.target.value)
        }, 'Ritme aangepast')} disabled={sub.status === 'gestopt'} aria-label="Elke hoeveel weken" className={inputCls} data-id="element-1372" data-mp="1q1ep8pd0zhib">
            {cadences.map(w => <option key={w} value={w} data-id="element-1373" data-mp="1bd1x63h26it3">
                Elke {w} weken
              </option>)}
          </select>
          <p className="mt-1.5 text-[12px] text-muted" data-id="element-1374" data-mp="16fa4364ogqnb">Alleen ritmes waarbij elke doos minstens {euro(policySub.minBoxValue)} is.</p>
          {active && <ol className="mt-4 space-y-2.5 text-[14px] text-ink" aria-label="Komende dozen" data-id="element-1375" data-mp="2hraxiychl39e">
              {refill.boxes.map((b, i) => <li key={b.date} data-id="element-1376" data-mp="2qbfrb63e1mpy" data-mpi={i}>
                  <span className="flex justify-between gap-3" data-id="element-1377" data-mp="2ubg990pp4g1r">
                    <span data-id="element-1378" data-mp="2u8ln94r1an68">{dayShort(b.date)}</span>
                    <span className="tabular-nums text-muted" data-id="element-1379" data-mp="2gc74qyauradm">
                      {b.lines.length ? euro(b.total) : 'geen doos'}
                      {giftBoxIndex(refill.boxes, untilGift) === i && <span className="ml-2 text-[12px] text-clay" data-id="element-1380" data-mp="38zb2qjbj2ofw">met cadeau</span>}
                    </span>
                  </span>
                  {b.lines.length > 0 && <span className="block text-[12px] text-muted" data-id="element-1381" data-mp="2za17wvpauro3">
                      {b.lines.map(l => `${l.qty > 1 ? `${l.qty}× ` : ''}${index.productById(l.productId)?.name ?? l.productId}`).join(', ')}
                    </span>}
                </li>)}
            </ol>}
        </Panel>
        {sub.status !== 'gestopt' && <DeleteButton label="Abonnement stoppen" warning={`Alleen op verzoek van ${name}`} onConfirm={() => act({
        type: 'cancel',
        reason: {
          code: 'door-salon'
        }
      }, 'Abonnement gestopt')} data-id="element-1382" data-mp="m42csi5l0so" />}
      </div>
    </div>;
}